/**
 * Props for the SplashHero component.
 */
interface SplashHeroProps {
  /** Small uppercase label above the headline (e.g. "Welcome"). */
  eyebrow: string;
  /** Main headline shown in white. */
  title: string;
  /** Supporting paragraph text below the headline. */
  subhead: string;
  /** Path to the brand illustration PNG. */
  illustrationSrc: string;
  /** Alt text for the illustration. */
  illustrationAlt: string;
}

/**
 * Dark hero band shown at the top of the splash page.
 * Two-column on desktop (text + illustration); stacks on tablet/mobile.
 * Background is `#1a1a1a`; the inner content is constrained to 1080px.
 */
export function SplashHero({
  eyebrow,
  title,
  subhead,
  illustrationSrc,
  illustrationAlt,
}: SplashHeroProps) {
  return (
    <section className="bg-[#1a1a1a] w-full">
      <div className="max-w-[1080px] mx-auto px-6 sm:px-8 py-6 md:py-10">
        <div className="flex flex-col-reverse md:flex-row items-center gap-10 md:gap-12">
          {/* Text column */}
          <div className="flex-1 text-center md:text-left">
            <p
              className="text-[13px] font-bold uppercase mb-4 text-[#f5ea29]"
              style={{
                fontFamily: '"Urbanist", "Helvetica", sans-serif',
                letterSpacing: '1.5px',
              }}
            >
              {eyebrow}
            </p>
            <h1
              className="text-[36px] md:text-[56px] font-bold text-white"
              style={{
                fontFamily: '"Urbanist", "Helvetica", sans-serif',
                lineHeight: 1.05,
              }}
            >
              {title}
            </h1>
            <p
              className="mt-5 text-[18px] text-white/70 max-w-[520px] mx-auto md:mx-0"
              style={{
                fontFamily: '"Urbanist", "Helvetica", sans-serif',
                lineHeight: 1.6,
              }}
            >
              {subhead}
            </p>
          </div>

          {/* Illustration column */}
          <div className="flex-1 flex justify-center md:justify-end">
            <img
              src={illustrationSrc}
              alt={illustrationAlt}
              className="w-full max-w-[12rem] md:max-w-[14rem]"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
